import { getSupabase } from "../components/supabaseClient";
import {
  supabaseUrl,
  bucketName_files,
  tableName,
  submissions_arrayUrls,
  getWTFFormsBaseURL,
  getWTFFormsConfig,
} from "../components/env";

/**
 * Adds a generated form URL to the patient's submissions_array column
 * @param {string} patientId - The patient's ID/UUID
 * @param {string} formPath - The form path or form ID (for WTF_forms)
 * @param {string} submissionId - The submission ID (for WTF_forms)
 * @param {boolean} isWTFForm - Flag to indicate if this is a WTF_form
 * @returns {Promise<boolean>} - True if successful, false otherwise
 */
export const addFormToSubmissions = async (
  patientId,
  formPath,
  submissionId = null,
  isWTFForm = false
) => {
  try {
    if (!patientId || !formPath) {
      console.error("Missing required parameters:", { patientId, formPath });
      return false;
    }

    const supabase = await getSupabase();
    let formUrl;

    // Handle different form types
    if (isWTFForm) {
      if (submissionId) {
        // Create WTF_forms submission URL format using environment configuration
        const config = getWTFFormsConfig();
        formUrl = `${config.baseURL}${config.formsPath}/${formPath}/submission/${submissionId}`;
      } else {
        // Create WTF_forms form URL (no submission yet)
        const config = getWTFFormsConfig();
        formUrl = `${config.baseURL}${config.formsPath}/${formPath}`;
      }
    } else {
      // Create the Supabase public URL for the form (legacy support)
      formUrl = `${supabaseUrl}/storage/v1/object/public/${bucketName_files}/${formPath}`;
    }

    console.log("Adding form to submissions:", {
      patientId,
      formPath,
      submissionId,
      isWTFForm,
      formUrl,
    });

    // First, get the current submissions_array for the patient
    const { data: patientData, error: fetchError } = await supabase
      .from(tableName)
      .select(submissions_arrayUrls)
      .eq("id", patientId)
      .single();

    if (fetchError) {
      console.error("Error fetching patient submissions:", fetchError);
      return false;
    }

    // Get current submissions array or initialize as empty array
    let currentSubmissions = patientData?.[submissions_arrayUrls] || [];

    // Ensure it's an array
    if (!Array.isArray(currentSubmissions)) {
      currentSubmissions = currentSubmissions ? [currentSubmissions] : [];
    }

    // Check if this form URL already exists in submissions
    const formExists = currentSubmissions.some((submission) => {
      // Check both URL and form_id for more robust duplicate detection
      const urlMatch =
        submission.url === formUrl || submission.url === formPath;
      const formIdMatch =
        isWTFForm && submission.form_id && submission.form_id === formPath;

      if (urlMatch || formIdMatch) {
        console.log("[DUPLICATE DETECTION] Form already exists:", {
          url: submission.url,
          form_id: submission.form_id,
          detected_by: urlMatch ? "URL match" : "Form ID match",
        });
        return true;
      }
      return false;
    });

    if (formExists) {
      console.log("[DUPLICATE DETECTION] Skipping duplicate form submission");
      return true; // Consider it successful since it's already there
    }

    // Add the new form to the submissions array
    const newSubmission = {
      url: formUrl,
      added_at: new Date().toISOString(),
      type: isWTFForm
        ? submissionId
          ? "wtf_form_submission"
          : "wtf_form"
        : "generated_form",
      form_id: isWTFForm ? formPath : null,
      submission_id: isWTFForm ? submissionId : null,
    };

    const updatedSubmissions = [...currentSubmissions, newSubmission];

    // Update the patient's submissions_array
    const { error: updateError } = await supabase
      .from(tableName)
      .update({ [submissions_arrayUrls]: updatedSubmissions })
      .eq("id", patientId);

    if (updateError) {
      console.error("Error updating submissions array:", updateError);
      return false;
    }

    console.log("Successfully added form to submissions:", {
      patientId,
      formUrl,
      totalSubmissions: updatedSubmissions.length,
    });

    return true;
  } catch (error) {
    console.error("Error in addFormToSubmissions:", error);
    return false;
  }
};

/**
 * Adds multiple generated form URLs to the patient's submissions_array column
 * @param {string} patientId - The patient's ID/UUID
 * @param {Array} formData - Array of form data objects with path, submissionId, and isWTFForm properties
 * @returns {Promise<number>} - Number of successfully added forms
 */
export const addMultipleFormsToSubmissions = async (patientId, formData) => {
  if (!patientId || !Array.isArray(formData) || formData.length === 0) {
    console.error("Invalid parameters for addMultipleFormsToSubmissions");
    return 0;
  }

  let successCount = 0;

  for (const formInfo of formData) {
    // Handle both legacy format (just path) and new format (object with properties)
    if (typeof formInfo === "string") {
      // Legacy format - treat as Supabase form
      const success = await addFormToSubmissions(patientId, formInfo);
      if (success) {
        successCount++;
      }
    } else {
      // New format with explicit properties
      const { formPath, submissionId, isWTFForm } = formInfo;
      const success = await addFormToSubmissions(
        patientId,
        formPath,
        submissionId,
        isWTFForm
      );
      if (success) {
        successCount++;
      }
    }
  }

  console.log(
    `Added ${successCount}/${formData.length} forms to submissions for patient ${patientId}`
  );
  return successCount;
};

/**
 * Extracts form information from the backend API response and adds them to submissions
 * @param {string} patientId - The patient's ID/UUID
 * @param {Object} apiResponse - The response from the form generation API
 * @returns {Promise<boolean>} - True if at least one form was added successfully
 */
export const processGeneratedFormResponse = async (patientId, apiResponse) => {
  try {
    if (!patientId || !apiResponse) {
      console.error(
        "Missing required parameters for processGeneratedFormResponse"
      );
      return false;
    }

    let formData = [];

    // Handle WTF_forms response format
    if (apiResponse.form_id) {
      // WTF_forms form response (may or may not have submission_id yet)
      formData.push({
        formPath: apiResponse.form_id,
        submissionId: apiResponse.submission_id || null,
        isWTFForm: true,
      });
    }
    // Handle legacy Supabase response formats
    else if (apiResponse.form_data && apiResponse.form_data.metadata) {
      // Single form response with metadata
      if (apiResponse.form_data.metadata.storage_path) {
        formData.push(apiResponse.form_data.metadata.storage_path);
      }
    } else if (apiResponse.storage_path) {
      // Direct storage path in response
      formData.push(apiResponse.storage_path);
    } else if (apiResponse.output_directory && apiResponse.patients_processed) {
      // Bulk generation response - extract paths for this specific patient
      const patientForms = apiResponse.patients_processed
        .filter(
          (patient) =>
            patient.patient_db_id === patientId ||
            patient.id_number === patientId ||
            patient.email === patientId
        )
        .map((patient) => {
          if (patient.form_id) {
            // WTF_forms format (may or may not have submission_id yet)
            return {
              formPath: patient.form_id,
              submissionId: patient.submission_id || null,
              isWTFForm: true,
            };
          } else {
            // Legacy Supabase format
            return patient.storage_path;
          }
        })
        .filter((path) => path);

      formData = patientForms;
    }

    if (formData.length === 0) {
      console.log("No form data found in API response");
      return false;
    }

    // Add all found forms to submissions
    const successCount = await addMultipleFormsToSubmissions(
      patientId,
      formData
    );

    return successCount > 0;
  } catch (error) {
    console.error("Error processing generated form response:", error);
    return false;
  }
};

/**
 * Adds a WTF_form submission to the patient's submissions_array column
 * @param {string} patientId - The patient's ID/UUID
 * @param {string} formId - The WTF_forms form ID
 * @param {string} submissionId - The WTF_forms submission ID
 * @returns {Promise<boolean>} - True if successful, false otherwise
 */
export const addWTFFormSubmission = async (patientId, formId, submissionId) => {
  return await addFormToSubmissions(patientId, formId, submissionId, true);
};
